import { motion } from 'framer-motion';
import { Header } from './Header';
import { Bottle } from './Bottle';
import { EssenceCarousel } from './EssenceCarousel';
import { FormulaPanel } from './FormulaPanel';
import { ActionButtons } from './ActionButtons';
import { usePerfume } from '../context/PerfumeContext';
import { Toaster } from './ui/sonner';
import { useLanguage } from '../context/LanguageContext';

export const PerfumeLab = () => {
  const { getMixedColor, fillPercent } = usePerfume();
  const { language } = useLanguage();
  const mixedColor = getMixedColor();

  return (
    <div className="min-h-screen bg-background noise-texture overflow-x-hidden">
      {/* Subtle ambient gradient based on current mixture */}
      <motion.div
        className="fixed inset-0 pointer-events-none opacity-30"
        animate={{
          background: mixedColor 
            ? `radial-gradient(ellipse at 50% 30%, ${mixedColor}20 0%, transparent 60%)`
            : 'transparent'
        }}
        transition={{ duration: 1.5 }}
      />

      <Header />

      {/* Main content */}
      <main className="relative pt-20 md:pt-28 pb-8 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          {/* Desktop layout: Bottle center, Formula right */}
          <div className="flex flex-col lg:flex-row items-center lg:items-start justify-center gap-6 lg:gap-16">
            
            {/* Left spacer (for balance on desktop) */}
            <div className="hidden lg:block w-full max-w-xs" />

            {/* Center: Bottle */}
            <motion.div 
              className="flex flex-col items-center"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
            >
              <Bottle />
              
              {/* Volume indicator below bottle (mobile) */}
              <motion.div 
                className="mt-4 text-center lg:hidden"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
              >
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary/50">
                  <div 
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: mixedColor || 'hsl(var(--muted))' }}
                  />
                  <span className="text-sm text-muted-foreground">
                    {Math.round(fillPercent)}% {language === 'jp' ? '充填' : 'filled'}
                  </span>
                </div>
              </motion.div>
            </motion.div>

            {/* Right: Formula Panel (desktop) */}
            <div className="hidden lg:block">
              <FormulaPanel />
            </div>
          </div>

          {/* Actions */}
          <div className="mt-6 md:mt-12 flex flex-col items-center">
            <ActionButtons />
          </div>

          {/* Essences Section */}
          <motion.section 
            className="mt-8 md:mt-16"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <EssenceCarousel />
          </motion.section>

          {/* Mobile Formula Panel */}
          <div className="lg:hidden mt-8 px-2">
            <FormulaPanel />
          </div>
        </div>
      </main>

      {/* Toast notifications */}
      <Toaster 
        position="bottom-center"
        toastOptions={{
          className: 'bg-card border-border text-foreground',
        }}
      />
    </div>
  );
};
